import React from 'react';
import { Helmet } from 'react-helmet-async';

const TermsOfService = () => {
  return (
    <div>
      <Helmet>
        <title>Terms of Service - Zorvian Associates | Legal Terms & Conditions</title>
        <meta name="description" content="Read Zorvian Associates' terms of service to understand the terms and conditions governing the use of our website and legal services." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0F172A] via-[#1e293b] to-[#0F172A] py-24 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-heading">
              Terms of Service
            </h1>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            
            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 font-heading">Agreement to Terms</h2>
            <p className="text-slate-600 mb-6">
              By accessing or using the Zorvian Associates website and services, you agree to be bound by these Terms of Service and 
              all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using 
              or accessing this site.
            </p>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">No Attorney-Client Relationship</h2>
            <p className="text-slate-600 mb-6">
              <strong>Important:</strong> Use of this website and the transmission of information through this website does not 
              create an attorney-client relationship between you and Zorvian Associates. An attorney-client relationship is only formed 
              when we have agreed to represent you in a particular matter and have confirmed this relationship in writing.
            </p>
            <p className="text-slate-600 mb-6">
              Do not send us confidential or time-sensitive information through this website until an attorney-client relationship 
              has been established. Any information you submit prior to establishing an attorney-client relationship may not be 
              protected by attorney-client privilege.
            </p>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Legal Services</h2>
            <h3 className="text-2xl font-bold text-[#0F172A] mb-3 mt-8">Scope of Services</h3>
            <p className="text-slate-600 mb-6">
              Zorvian Associates provides legal services in various practice areas as described on our website. The specific scope of 
              services for each client matter will be defined in a written engagement agreement.
            </p>

            <h3 className="text-2xl font-bold text-[#0F172A] mb-3 mt-8">No Guarantee of Results</h3>
            <p className="text-slate-600 mb-6">
              While we strive to achieve the best possible outcomes for our clients, we cannot guarantee specific results. 
              Past performance is not indicative of future results. Every legal matter is unique and depends on numerous factors.
            </p>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Website Use</h2>
            <h3 className="text-2xl font-bold text-[#0F172A] mb-3 mt-8">Information Accuracy</h3>
            <p className="text-slate-600 mb-6">
              We make every effort to ensure that the information on this website is accurate and current. However, laws and 
              regulations change frequently, and information on this website may not reflect the most recent legal developments. 
              This website is for informational purposes only and does not constitute legal advice.
            </p>

            <h3 className="text-2xl font-bold text-[#0F172A] mb-3 mt-8">Prohibited Uses</h3>
            <p className="text-slate-600 mb-4">
              You may not use this website:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>In any way that violates any applicable federal, state, local, or international law</li>
              <li>To transmit or procure the sending of any advertising or promotional material</li>
              <li>To impersonate or attempt to impersonate Zorvian Associates, our employees, or other users</li>
              <li>To engage in any conduct that restricts or inhibits anyone's use of the website</li>
              <li>To introduce any viruses, trojan horses, worms, or other malicious code</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Intellectual Property Rights</h2>
            <p className="text-slate-600 mb-6">
              Unless otherwise indicated, this website and all content, features, and functionality (including but not limited 
              to all information, software, text, displays, images, video, and audio) are owned by Zorvian Associates and are protected 
              by United States and international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>
            <p className="text-slate-600 mb-6">
              You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, 
              republish, download, store, or transmit any of the material on our website without our prior written consent.
            </p>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Links to Other Websites</h2>
            <p className="text-slate-600 mb-6">
              Our website may contain links to third-party websites that are not owned or controlled by Zorvian Associates. We have no 
              control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party 
              websites. We strongly advise you to read the terms and conditions and privacy policies of any third-party websites 
              you visit.
            </p>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Limitation of Liability</h2>
            <p className="text-slate-600 mb-6">
              In no event shall Zorvian Associates, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable 
              for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of 
              profits, data, use, goodwill, or other intangible losses, resulting from:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>Your access to or use of or inability to access or use the website</li>
              <li>Any conduct or content of any third party on the website</li>
              <li>Any content obtained from the website</li>
              <li>Unauthorized access, use, or alteration of your transmissions or content</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Disclaimer</h2>
            <p className="text-slate-600 mb-6">
              Your use of the website is at your sole risk. The website is provided on an "AS IS" and "AS AVAILABLE" basis. 
              The website is provided without warranties of any kind, whether express or implied, including, but not limited to, 
              implied warranties of merchantability, fitness for a particular purpose, non-infringement, or course of performance.
            </p>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Governing Law</h2>
            <p className="text-slate-600 mb-6">
              These Terms shall be governed and construed in accordance with the laws of the State of California, United States, 
              without regard to its conflict of law provisions. Any legal action or proceeding arising under these Terms will be 
              brought exclusively in the federal or state courts located in California.
            </p>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Changes to Terms</h2>
            <p className="text-slate-600 mb-6">
              We reserve the right to modify or replace these Terms at any time at our sole discretion. If a revision is material, 
              we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change 
              will be determined at our sole discretion.
            </p>
            <p className="text-slate-600 mb-6">
              By continuing to access or use our website after revisions become effective, you agree to be bound by the revised 
              terms. If you do not agree to the new terms, you are no longer authorized to use the website.
            </p>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Severability</h2>
            <p className="text-slate-600 mb-6">
              If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted 
              to accomplish the objectives of such provision to the greatest extent possible under applicable law, and the remaining 
              provisions will continue in full force and effect.
            </p>

            <h2 className="text-3xl font-bold text-[#0F172A] mb-4 mt-12 font-heading">Contact Us</h2>
            <p className="text-slate-600 mb-4">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-slate-50 p-6 rounded-lg mb-6">
              <p className="text-slate-700 mb-2"><strong>Email:</strong> Info@zorvianassociates.com</p>
              <p className="text-slate-700 mb-2"><strong>Phone:</strong> +1 (844) 921-2228</p>
              <p className="text-slate-700"><strong>Address:</strong> 166 Geary St Ste 1500 #1787, San Francisco, CA 94108, USA</p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
